export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export class Vec3 implements Vec3Like {
  constructor(
    public readonly x = 0,
    public readonly y = 0,
    public readonly z = 0,
  ) {}

  static from(value: Vec3Like): Vec3 {
    return new Vec3(value.x, value.y, value.z);
  }

  add(other: Vec3Like): Vec3 {
    return new Vec3(this.x + other.x, this.y + other.y, this.z + other.z);
  }

  sub(other: Vec3Like): Vec3 {
    return new Vec3(this.x - other.x, this.y - other.y, this.z - other.z);
  }

  scale(factor: number): Vec3 {
    return new Vec3(this.x * factor, this.y * factor, this.z * factor);
  }

  dot(other: Vec3Like): number {
    return this.x * other.x + this.y * other.y + this.z * other.z;
  }

  cross(other: Vec3Like): Vec3 {
    return new Vec3(
      this.y * other.z - this.z * other.y,
      this.z * other.x - this.x * other.z,
      this.x * other.y - this.y * other.x,
    );
  }

  lengthSquared(): number {
    return this.dot(this);
  }

  length(): number {
    return Math.sqrt(this.lengthSquared());
  }

  distanceTo(other: Vec3Like): number {
    return this.sub(other).length();
  }

  normalize(): Vec3 {
    const magnitude = this.length();
    return magnitude > Number.EPSILON ? this.scale(1 / magnitude) : new Vec3();
  }

  lerp(other: Vec3Like, t: number): Vec3 {
    return new Vec3(
      this.x + (other.x - this.x) * t,
      this.y + (other.y - this.y) * t,
      this.z + (other.z - this.z) * t,
    );
  }

  min(other: Vec3Like): Vec3 {
    return new Vec3(
      Math.min(this.x, other.x),
      Math.min(this.y, other.y),
      Math.min(this.z, other.z),
    );
  }

  max(other: Vec3Like): Vec3 {
    return new Vec3(
      Math.max(this.x, other.x),
      Math.max(this.y, other.y),
      Math.max(this.z, other.z),
    );
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function rotateEuler(vector: Vec3, rotation: Vec3): Vec3 {
  const cx = Math.cos(rotation.x);
  const sx = Math.sin(rotation.x);
  const cy = Math.cos(rotation.y);
  const sy = Math.sin(rotation.y);
  const cz = Math.cos(rotation.z);
  const sz = Math.sin(rotation.z);

  const afterX = new Vec3(
    vector.x,
    vector.y * cx - vector.z * sx,
    vector.y * sx + vector.z * cx,
  );
  const afterY = new Vec3(
    afterX.x * cy + afterX.z * sy,
    afterX.y,
    -afterX.x * sy + afterX.z * cy,
  );
  return new Vec3(
    afterY.x * cz - afterY.y * sz,
    afterY.x * sz + afterY.y * cz,
    afterY.z,
  );
}

export function rotateAroundEuler(vector: Vec3, rotation: Vec3, pivot = new Vec3()): Vec3 {
  return rotateEuler(vector.sub(pivot), rotation).add(pivot);
}
